/**
 * Admin · Products System (P4) · Catalog detail.
 *
 * - Edit catalog meta + branding company.
 * - Import products from ANY URL (AI-assisted) → review → add.
 * - Manage items (price, remove).
 * - Download a branded catalogue PDF.
 * - Raise a Proforma / Invoice in Finance from selected items.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import ProductPicker from '../../../components/admin/ProductPicker';
import CustomerPicker from '../../../components/shared/CustomerPicker';
import {
  Loader2, Link2, Download, FileText, Trash2, Plus, X, Save, Package,
  Sparkles, CheckCircle2, ExternalLink, Wand2, Pencil,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CURRENCIES = ['AED', 'USD', 'EUR', 'GBP', 'AOA'];

const CatalogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [catalog, setCatalog] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingMeta, setSavingMeta] = useState(false);

  // Import modal
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [addingDraft, setAddingDraft] = useState(false);

  // Invoice modal
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [invForm, setInvForm] = useState({
    status: 'proforma', tax_rate: 5, customer_id: '', customer_name: '', customer_company: '',
    customer_email: '', customer_phone: '', customer_website: '', customer_trn: '',
    customer_tax_id_type: '', customer_address: '',
  });
  const [selected, setSelected] = useState({}); // item_id -> qty
  const [extraLines, setExtraLines] = useState([]); // [{product_id, product_sku, name, unit_price, currency, quantity}]
  const [wmBusy, setWmBusy] = useState({}); // 'draft' | item_id -> bool
  const [pdfBusy, setPdfBusy] = useState(null); // 'en' | 'pt' -> busy
  const [editingItemId, setEditingItemId] = useState(null); // null = import mode
  const [savingEdit, setSavingEdit] = useState(false);

  const resolveImg = (u) => (u && u.startsWith('/api/') ? `${API_URL}${u}` : u);

  const removeWatermarkFor = async (imageUrl, apply) => {
    try {
      const start = await axios.post(`${API_URL}/api/admin/catalogs/remove-watermark`,
        { url: resolveImg(imageUrl) }, auth);
      const jobId = start.data.job_id;
      // Poll until the AI edit finishes (cold-start edits + retries can take a while).
      for (let i = 0; i < 90; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        const s = await axios.get(`${API_URL}/api/admin/catalogs/watermark-status/${jobId}`, auth);
        if (s.data.status === 'done') {
          await apply(s.data.image_url);
          toast({ title: 'Watermark removed' });
          return;
        }
        if (s.data.status === 'error') {
          throw new Error(s.data.error || 'AI editor failed');
        }
      }
      throw new Error('Timed out — please try again');
    } catch (e) {
      toast({ title: 'Could not remove watermark', description: e.response?.data?.detail || e.message || 'AI editor failed', variant: 'destructive' });
    }
  };

  const draftWatermark = async () => {
    if (!draft) return;
    const urls = [];
    if (draft.image_url) urls.push(draft.image_url);
    (draft.gallery_urls || []).forEach((g) => { if (!urls.includes(g)) urls.push(g); });
    if (urls.length === 0) return;
    setWmBusy((w) => ({ ...w, draft: true }));
    try {
      const start = await axios.post(`${API_URL}/api/admin/catalogs/clean-images`, { urls }, auth);
      const jobId = start.data.job_id;
      for (let i = 0; i < 160; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        const s = await axios.get(`${API_URL}/api/admin/catalogs/watermark-status/${jobId}`, auth);
        if (s.data.status === 'done') {
          const cleaned = s.data.images || [];
          const map = {};
          urls.forEach((u, idx) => { map[u] = cleaned[idx] || u; });
          setDraft((d) => ({
            ...d,
            image_url: map[d.image_url] || d.image_url,
            gallery_urls: (d.gallery_urls || []).map((g) => map[g] || g),
          }));
          toast({ title: 'Photos cleaned', description: `Watermark removed from ${s.data.cleaned} photo(s)` });
          return;
        }
        if (s.data.status === 'error') throw new Error(s.data.error || 'AI editor failed');
      }
      throw new Error('Timed out — please try again');
    } catch (e) {
      toast({ title: 'Could not remove watermark', description: e.response?.data?.detail || e.message || 'AI editor failed', variant: 'destructive' });
    } finally {
      setWmBusy((w) => ({ ...w, draft: false }));
    }
  };

  const deleteDraftPhoto = (url) => {
    setDraft((d) => {
      const gallery = (d.gallery_urls || []).filter((g) => g !== url);
      const image_url = d.image_url === url ? (gallery[0] || null) : d.image_url;
      return { ...d, gallery_urls: gallery, image_url };
    });
  };

  const updateDraftSpec = (idx, field, value) => {
    setDraft((d) => {
      const specs = [...(d.specs || [])];
      specs[idx] = { ...specs[idx], [field]: value };
      return { ...d, specs };
    });
  };

  const deleteDraftSpec = (idx) => {
    setDraft((d) => ({ ...d, specs: (d.specs || []).filter((_, i) => i !== idx) }));
  };

  const addDraftSpec = () => {
    setDraft((d) => ({ ...d, specs: [...(d.specs || []), { label: '', value: '' }] }));
  };

  const openEditItem = (it) => {
    setEditingItemId(it.id);
    setDraft({
      name: it.name || '',
      brand: it.brand || '',
      description: it.description || '',
      image_url: it.image_url || null,
      gallery_urls: it.gallery_urls || [],
      specs: (it.specs || []).map((s) => ({ ...s })),
      unit_price: it.unit_price ?? '',
      currency: it.currency || catalog.currency,
    });
    setImportUrl('');
    setShowImport(true);
  };

  const closeModal = () => {
    setShowImport(false);
    setDraft(null);
    setEditingItemId(null);
  };

  const saveDraftEdit = async () => {
    if (!editingItemId || !draft) return;
    setSavingEdit(true);
    try {
      const body = {
        name: draft.name,
        brand: draft.brand || '',
        description: draft.description || '',
        image_url: draft.image_url || null,
        gallery_urls: draft.gallery_urls || [],
        specs: draft.specs || [],
        unit_price: draft.unit_price === '' || draft.unit_price == null ? null : parseFloat(draft.unit_price),
        currency: draft.currency || catalog.currency,
      };
      const r = await axios.put(`${API_URL}/api/admin/catalogs/${id}/items/${editingItemId}`, body, auth);
      setCatalog(r.data);
      toast({ title: 'Product updated' });
      closeModal();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Could not save changes', variant: 'destructive' });
    } finally {
      setSavingEdit(false);
    }
  };

  const itemWatermark = async (it) => {
    if (!it.image_url) return;
    setWmBusy((w) => ({ ...w, [it.id]: true }));
    await removeWatermarkFor(it.image_url, async (u) => {
      const r = await axios.put(`${API_URL}/api/admin/catalogs/${id}/items/${it.id}`, { image_url: u }, auth);
      setCatalog(r.data);
    });
    setWmBusy((w) => ({ ...w, [it.id]: false }));
  };

  const cleanGallery = async (it) => {
    const key = `gal-${it.id}`;
    setWmBusy((w) => ({ ...w, [key]: true }));
    try {
      const start = await axios.post(`${API_URL}/api/admin/catalogs/${id}/items/${it.id}/clean-gallery`, {}, auth);
      const jobId = start.data.job_id;
      for (let i = 0; i < 160; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        const s = await axios.get(`${API_URL}/api/admin/catalogs/watermark-status/${jobId}`, auth);
        if (s.data.status === 'done') {
          await load();
          toast({ title: 'Photos cleaned', description: `Watermark removed from ${s.data.cleaned} photo(s)` });
          return;
        }
        if (s.data.status === 'error') throw new Error(s.data.error || 'AI editor failed');
      }
      throw new Error('Timed out — please try again');
    } catch (e) {
      toast({ title: 'Could not clean photos', description: e.response?.data?.detail || e.message || 'AI editor failed', variant: 'destructive' });
    } finally {
      setWmBusy((w) => ({ ...w, [key]: false }));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [c, co] = await Promise.all([
        axios.get(`${API_URL}/api/admin/catalogs/${id}`, auth),
        axios.get(`${API_URL}/api/procurement/companies`, auth),
      ]);
      setCatalog(c.data);
      setCompanies(co.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load catalog', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const r = await axios.put(`${API_URL}/api/admin/catalogs/${id}`, {
        name: catalog.name, description: catalog.description,
        company_id: catalog.company_id, currency: catalog.currency,
      }, auth);
      setCatalog(r.data);
      toast({ title: 'Saved' });
    } catch (e) {
      toast({ title: 'Error', description: 'Could not save', variant: 'destructive' });
    } finally {
      setSavingMeta(false);
    }
  };

  const runImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    setDraft(null);
    try {
      const r = await axios.post(`${API_URL}/api/admin/catalogs/import-url`, { url: importUrl.trim() }, auth);
      setDraft({ ...r.data, unit_price: r.data.price ?? '', currency: r.data.currency || catalog.currency });
      toast({ title: 'Product found', description: r.data.name });
    } catch (e) {
      toast({ title: 'Import failed', description: e.response?.data?.detail || 'Could not read this URL', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const addDraft = async () => {
    if (!draft) return;
    setAddingDraft(true);
    try {
      const payload = {
        name: draft.name, brand: draft.brand || '', description: draft.description || '',
        image_url: draft.image_url || null, gallery_urls: draft.gallery_urls || [],
        specs: draft.specs || [], source_url: draft.source_url || null,
        unit_price: draft.unit_price === '' || draft.unit_price === null ? null : Number(draft.unit_price),
        currency: draft.currency || catalog.currency,
      };
      const r = await axios.post(`${API_URL}/api/admin/catalogs/${id}/items`, payload, auth);
      setCatalog(r.data);
      setDraft(null);
      setImportUrl('');
      setShowImport(false);
      toast({ title: 'Added to catalog' });
    } catch (e) {
      toast({ title: 'Error', description: 'Could not add product', variant: 'destructive' });
    } finally {
      setAddingDraft(false);
    }
  };

  const updateItemPrice = async (itemId, price) => {
    try {
      const r = await axios.put(`${API_URL}/api/admin/catalogs/${id}/items/${itemId}`,
        { unit_price: price === '' ? null : Number(price) }, auth);
      setCatalog(r.data);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not update price', variant: 'destructive' });
    }
  };

  const removeItem = async (itemId) => {
    if (!window.confirm('Remove this product from the catalog?')) return;
    try {
      const r = await axios.delete(`${API_URL}/api/admin/catalogs/${id}/items/${itemId}`, auth);
      setCatalog(r.data);
      toast({ title: 'Removed' });
    } catch (e) {
      toast({ title: 'Error', description: 'Could not remove', variant: 'destructive' });
    }
  };

  const downloadPdf = async (lang = 'en') => {
    setPdfBusy(lang);
    try {
      const res = await fetch(`${API_URL}/api/admin/catalogs/${id}/pdf?lang=${lang}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('PDF failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(catalog?.name || 'catalogue').replace(/\s+/g, '_')}_${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
      toast({ title: lang === 'pt' ? 'Catálogo PDF (PT) descarregado' : 'Catalogue PDF (EN) downloaded' });
    } catch (e) {
      toast({ title: 'Error', description: 'Could not generate PDF', variant: 'destructive' });
    } finally {
      setPdfBusy(null);
    }
  };

  const openInvoice = () => {
    const sel = {};
    (catalog.items || []).forEach((it) => { sel[it.id] = 1; });
    setSelected(sel);
    setExtraLines([]);
    setInvoiceResult(null);
    setShowInvoice(true);
  };

  const handleInvoiceCustomer = (c) => {
    if (!c) return;
    const addr = c.address || {};
    const addrStr = typeof addr === 'string' ? addr
      : [addr.street, addr.line1, addr.line2, addr.city, addr.state, addr.postal_code, addr.country]
          .map((p) => (p || '').toString().trim()).filter(Boolean).join(', ');
    setInvForm((f) => ({
      ...f,
      customer_id: c.id || '',
      customer_name: c.name || c.company_name || '',
      customer_company: c.company_name || '',
      customer_email: c.email || '',
      customer_phone: c.phone || '',
      customer_website: c.website || '',
      customer_trn: c.trn || '',
      customer_tax_id_type: c.tax_id_type || '',
      customer_address: addrStr || '',
    }));
    toast({ title: 'Customer loaded', description: `${c.company_name || c.name} — details pre-filled` });
  };

  const addExtraProduct = (p) => {
    if (!p) return;
    setExtraLines((rows) => {
      if (rows.some((r) => r.product_id === p.id)) return rows;
      return [...rows, {
        product_id: p.id,
        product_sku: p.sku || '',
        name: p.name || 'Product',
        unit_price: p.selling_price ?? p.default_price ?? 0,
        currency: (p.default_currency || catalog.currency || 'AED'),
        quantity: 1,
      }];
    });
  };

  const submitInvoice = async () => {
    if (!invForm.customer_name.trim()) {
      toast({ title: 'Customer name required', variant: 'destructive' });
      return;
    }
    const catLines = Object.entries(selected)
      .filter(([, qty]) => Number(qty) > 0)
      .map(([item_id, qty]) => ({ item_id, quantity: Number(qty) }));
    const extra = extraLines
      .filter((r) => Number(r.quantity) > 0)
      .map((r) => ({
        product_id: r.product_id, product_sku: r.product_sku, name: r.name,
        unit_price: Number(r.unit_price) || 0, quantity: Number(r.quantity),
      }));
    const lines = [...catLines, ...extra];
    if (lines.length === 0) {
      toast({ title: 'Select at least one product', variant: 'destructive' });
      return;
    }
    setInvoicing(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/catalogs/${id}/invoice`, {
        ...invForm, tax_rate: Number(invForm.tax_rate) || 0, lines,
      }, auth);
      setInvoiceResult(r.data);
      toast({ title: `${r.data.invoice_type === 'proforma' ? 'Proforma' : 'Invoice'} created`, description: r.data.invoice_number });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Could not create invoice', variant: 'destructive' });
    } finally {
      setInvoicing(false);
    }
  };

  if (loading || !catalog) {
    return <div className="flex items-center justify-center py-24 text-[#6C757D]"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>;
  }

  const items = catalog.items || [];

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="catalog-detail-page">
      <BackButton />

      {/* Meta */}
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-bold uppercase tracking-wide text-[#6C757D]">Catalog name</label>
            <input value={catalog.name} onChange={(e) => setCatalog({ ...catalog, name: e.target.value })}
                   data-testid="catalog-detail-name"
                   className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-lg font-extrabold text-[#0F172A] focus:outline-none focus:border-[#00B4D8]" />
          </div>
          <div className="w-full lg:w-56">
            <label className="text-xs font-bold uppercase tracking-wide text-[#6C757D]">Brand / Issuing company</label>
            <select value={catalog.company_id || ''} onChange={(e) => setCatalog({ ...catalog, company_id: e.target.value })}
                    data-testid="catalog-detail-company"
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
              <option value="">Default company</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.trading_name || c.legal_name}</option>)}
            </select>
          </div>
          <div className="w-full lg:w-28">
            <label className="text-xs font-bold uppercase tracking-wide text-[#6C757D]">Currency</label>
            <select value={catalog.currency} onChange={(e) => setCatalog({ ...catalog, currency: e.target.value })}
                    data-testid="catalog-detail-currency"
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Button onClick={saveMeta} disabled={savingMeta} data-testid="save-catalog-meta"
                  variant="outline" className="whitespace-nowrap">
            {savingMeta ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}
          </Button>
        </div>
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <Button onClick={() => { setShowImport(true); setDraft(null); setImportUrl(''); setEditingItemId(null); }}
                  data-testid="import-url-btn" className="bg-[#0F172A] hover:bg-[#1E293B]">
            <Link2 className="w-4 h-4 mr-2" /> Import from URL
          </Button>
          <Button onClick={() => downloadPdf('en')} disabled={items.length === 0 || pdfBusy} data-testid="download-pdf-btn"
                  variant="outline">
            {pdfBusy === 'en' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Catalogue PDF (EN)
          </Button>
          <Button onClick={() => downloadPdf('pt')} disabled={items.length === 0 || pdfBusy} data-testid="download-pdf-pt-btn"
                  variant="outline">
            {pdfBusy === 'pt' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            PDF (PT)
          </Button>
          <Button onClick={openInvoice} disabled={items.length === 0} data-testid="create-invoice-btn"
                  className="bg-[#FF6B35] hover:bg-[#F97316]">
            <FileText className="w-4 h-4 mr-2" /> Create Invoice / Proforma
          </Button>
        </div>
      </div>

      {/* Items */}
      <div className="mt-6">
        <h2 className="text-sm font-bold uppercase tracking-wide text-[#6C757D] mb-3 flex items-center gap-2">
          <Package className="w-4 h-4" /> Products ({items.length})
        </h2>
        {items.length === 0 ? (
          <div className="border-2 border-dashed border-slate-200 rounded-2xl p-14 text-center text-[#6C757D]" data-testid="catalog-items-empty">
            No products yet. Use <b>Import from URL</b> to add one.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="catalog-items-grid">
            {items.map((it) => (
              <div key={it.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col" data-testid={`catalog-item-${it.id}`}>
                <div className="h-40 bg-slate-50 flex items-center justify-center overflow-hidden relative">
                  {it.image_url
                    ? <img src={resolveImg(it.image_url)} alt={it.name} className="h-full w-full object-contain" />
                    : <Package className="w-10 h-10 text-slate-300" />}
                  {it.image_url && (
                    <button onClick={() => itemWatermark(it)} disabled={wmBusy[it.id]}
                            data-testid={`item-watermark-${it.id}`} title="Remove watermark (AI)"
                            className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 border border-slate-200 text-slate-600 hover:text-[#FF6B35] shadow-sm">
                      {wmBusy[it.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  {it.brand && <span className="text-[10px] font-extrabold uppercase tracking-wide text-[#FF6B35]">{it.brand}</span>}
                  <h3 className="font-bold text-[#0F172A] text-sm leading-snug line-clamp-2">{it.name}</h3>
                  <p className="text-xs text-[#6C757D] mt-1 line-clamp-2">{it.description}</p>
                  {(it.specs || []).length > 0 && (
                    <span className="text-[11px] text-slate-400 mt-1">{(it.specs || []).length} specs</span>
                  )}
                  {it.master_product_id && it.sku && (
                    <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-[#198754]" data-testid={`item-registry-link-${it.id}`}
                          title="Linked to the Master Product Registry — available in all invoice generators">
                      <Package className="w-3 h-3" /> Registry: <code className="bg-emerald-50 px-1 rounded border border-emerald-200">{it.sku}</code>
                    </span>
                  )}
                  {(it.gallery_urls || []).length > 1 && (
                    <button onClick={() => cleanGallery(it)} disabled={wmBusy[`gal-${it.id}`]}
                            data-testid={`clean-gallery-${it.id}`}
                            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#00B4D8] hover:text-[#0F172A] disabled:opacity-60">
                      {wmBusy[`gal-${it.id}`]
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cleaning all photos…</>
                        : <><Sparkles className="w-3.5 h-3.5" /> Remove watermark from all {(it.gallery_urls || []).length} photos</>}
                    </button>
                  )}
                  <div className="mt-auto pt-3 flex items-center gap-2">
                    <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                      <span className="px-2 text-[11px] text-slate-500 bg-slate-50">{it.currency || catalog.currency}</span>
                      <input type="number" defaultValue={it.unit_price ?? ''} placeholder="Price"
                             data-testid={`item-price-${it.id}`}
                             onBlur={(e) => updateItemPrice(it.id, e.target.value)}
                             className="w-24 px-2 py-1.5 text-sm focus:outline-none" />
                    </div>
                    {it.source_url && (
                      <a href={it.source_url} target="_blank" rel="noreferrer"
                         className="p-1.5 text-slate-400 hover:text-[#00B4D8]" title="Source">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <button onClick={() => openEditItem(it)} data-testid={`edit-item-${it.id}`}
                            title="Edit product"
                            className="ml-auto p-1.5 rounded-lg text-slate-400 hover:text-[#00B4D8] hover:bg-cyan-50">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeItem(it.id)} data-testid={`remove-item-${it.id}`}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Import modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="import-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-extrabold text-[#0F172A] flex items-center gap-2">
                {editingItemId
                  ? <><Pencil className="w-5 h-5 text-[#00B4D8]" /> Edit product</>
                  : <><Sparkles className="w-5 h-5 text-[#FF6B35]" /> Import product from URL</>}
              </h2>
              <button onClick={closeModal} className="p-1.5 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            {!editingItemId && (
              <>
                <div className="flex gap-2">
                  <input value={importUrl} onChange={(e) => setImportUrl(e.target.value)}
                         data-testid="import-url-input" placeholder="https://… product page"
                         className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#00B4D8]" />
                  <Button onClick={runImport} disabled={importing} data-testid="import-url-fetch"
                          className="bg-[#0F172A] hover:bg-[#1E293B]">
                    {importing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Reading…</> : 'Fetch'}
                  </Button>
                </div>
                <p className="text-xs text-[#6C757D] mt-2">Works with most product pages. Some sites block automated reads.</p>
              </>
            )}

            {draft && (
              <div className="mt-5 border border-slate-200 rounded-xl p-4" data-testid="import-draft-preview">
                <div className="flex gap-4">
                  <div className="w-28 h-28 bg-slate-50 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 relative">
                    {draft.image_url ? <img src={resolveImg(draft.image_url)} alt={draft.name} className="w-full h-full object-contain" /> : <Package className="w-8 h-8 text-slate-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <input value={draft.brand || ''} onChange={(e) => setDraft({ ...draft, brand: e.target.value })}
                           data-testid="draft-brand-input" placeholder="+ Brand (optional)"
                           className="w-full text-[11px] font-extrabold uppercase text-[#FF6B35] placeholder:text-slate-300 placeholder:font-semibold border border-slate-200 rounded px-2 py-1 focus:border-[#00B4D8] focus:outline-none bg-white mb-1" />
                    <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                           data-testid="draft-name-input"
                           className="w-full font-bold text-[#0F172A] border-b border-slate-200 focus:outline-none focus:border-[#00B4D8] pb-1" />
                    <textarea value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                              rows={2} className="w-full text-xs text-[#6C757D] mt-2 border border-slate-200 rounded p-2 focus:outline-none" />
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <select value={draft.currency || catalog.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
                              className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white">
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input type="number" value={draft.unit_price} onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })}
                             data-testid="draft-price-input" placeholder="Selling price"
                             className="w-32 px-2 py-1.5 border border-slate-200 rounded text-sm focus:outline-none" />
                      <Button variant="outline" size="sm" onClick={draftWatermark} disabled={wmBusy.draft}
                              data-testid="draft-remove-watermark" className="h-9">
                        {wmBusy.draft ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Cleaning…</> : <><Wand2 className="w-4 h-4 mr-1" /> Remove watermark (all photos)</>}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Gallery — pick the main photo or delete photos */}
                {(draft.gallery_urls || []).length > 0 && (
                  <div className="flex gap-2 mt-3 flex-wrap" data-testid="draft-gallery">
                    {draft.gallery_urls.map((g, i) => (
                      <div key={i} className="relative">
                        <button onClick={() => setDraft({ ...draft, image_url: g })}
                                data-testid={`draft-thumb-${i}`}
                                className={`w-14 h-14 rounded-lg border overflow-hidden block ${resolveImg(draft.image_url) === resolveImg(g) ? 'border-[#00B4D8] ring-2 ring-[#00B4D8]' : 'border-slate-200 hover:border-slate-300'}`}>
                          <img src={resolveImg(g)} alt="" className="w-full h-full object-cover" />
                        </button>
                        <button onClick={() => deleteDraftPhoto(g)} data-testid={`draft-thumb-del-${i}`} title="Delete photo"
                                className="absolute -top-1.5 -right-1.5 bg-white border border-slate-200 rounded-full p-0.5 text-slate-400 hover:text-red-500 hover:border-red-300 shadow-sm">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Technical specifications — editable */}
                <details className="mt-3" data-testid="draft-specs" open>
                  <summary className="text-xs font-bold text-[#0F172A] cursor-pointer select-none">
                    {(draft.specs || []).length} technical specifications
                  </summary>
                  <div className="mt-2 max-h-56 overflow-y-auto border border-slate-100 rounded-lg divide-y">
                    {(draft.specs || []).map((s, i) => (
                      <div key={i} className="flex gap-2 items-center px-2 py-1.5">
                        <input value={s.label} onChange={(e) => updateDraftSpec(i, 'label', e.target.value)}
                               data-testid={`draft-spec-label-${i}`} placeholder="Label"
                               className="flex-1 min-w-0 text-[11px] text-slate-600 border-b border-transparent hover:border-slate-200 focus:border-[#00B4D8] focus:outline-none bg-transparent" />
                        <input value={s.value} onChange={(e) => updateDraftSpec(i, 'value', e.target.value)}
                               data-testid={`draft-spec-value-${i}`} placeholder="Value"
                               className="flex-1 min-w-0 text-[11px] text-[#0F172A] font-medium text-right border-b border-transparent hover:border-slate-200 focus:border-[#00B4D8] focus:outline-none bg-transparent" />
                        <button onClick={() => deleteDraftSpec(i)} data-testid={`draft-spec-del-${i}`} title="Delete specification"
                                className="text-slate-300 hover:text-red-500 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  <button onClick={addDraftSpec} data-testid="draft-spec-add"
                          className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#00B4D8] hover:text-[#0F172A]">
                    <Plus className="w-3.5 h-3.5" /> Add specification
                  </button>
                </details>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={closeModal}>{editingItemId ? 'Cancel' : 'Discard'}</Button>
                  {editingItemId ? (
                    <Button onClick={saveDraftEdit} disabled={savingEdit} data-testid="save-item-edit-btn" className="bg-[#00B4D8] hover:bg-[#0096B4]">
                      {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save changes</>}
                    </Button>
                  ) : (
                    <Button onClick={addDraft} disabled={addingDraft} data-testid="add-draft-btn" className="bg-[#FF6B35] hover:bg-[#F97316]">
                      {addingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add to catalog</>}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invoice modal */}
      {showInvoice && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="invoice-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-extrabold text-[#0F172A]">Create Invoice / Proforma</h2>
              <button onClick={() => setShowInvoice(false)} className="p-1.5 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>

            {invoiceResult ? (
              <div className="text-center py-6" data-testid="invoice-result">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h3 className="font-extrabold text-[#0F172A] text-lg">
                  {invoiceResult.invoice_type === 'proforma' ? 'Proforma' : 'Invoice'} created
                </h3>
                <p className="text-sm text-[#6C757D] mt-1">Number: <b className="font-mono text-[#023E8A]">{invoiceResult.invoice_number}</b></p>
                <p className="text-sm text-[#6C757D]">Total: <b>{invoiceResult.currency} {Number(invoiceResult.total).toLocaleString()}</b></p>
                <div className="flex justify-center gap-2 mt-5">
                  <Button variant="outline" onClick={() => setShowInvoice(false)}>Close</Button>
                  <Button onClick={() => navigate(`/admin/finance/invoices/${invoiceResult.invoice_id}`)}
                          data-testid="open-finance-invoice" className="bg-[#0F172A] hover:bg-[#1E293B]">
                    Open in Finance <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <label className="text-xs font-bold uppercase text-[#6C757D] mb-1 block">Select customer from platform</label>
                  <CustomerPicker
                    value={invForm.customer_id}
                    onSelect={handleInvoiceCustomer}
                    onCreateNew={() => window.open('/admin/finance/customers', '_blank')}
                    testId="inv-customer-picker"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold uppercase text-[#6C757D]">Customer name *</label>
                    <input value={invForm.customer_name} onChange={(e) => setInvForm({ ...invForm, customer_name: e.target.value })}
                           data-testid="inv-customer-name"
                           className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#6C757D]">Company</label>
                    <input value={invForm.customer_company} onChange={(e) => setInvForm({ ...invForm, customer_company: e.target.value })}
                           className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#6C757D]">Email</label>
                    <input value={invForm.customer_email} onChange={(e) => setInvForm({ ...invForm, customer_email: e.target.value })}
                           className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#6C757D]">Phone</label>
                    <input value={invForm.customer_phone} onChange={(e) => setInvForm({ ...invForm, customer_phone: e.target.value })}
                           data-testid="inv-customer-phone"
                           className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#6C757D]">TRN / Tax ID</label>
                    <input value={invForm.customer_trn} onChange={(e) => setInvForm({ ...invForm, customer_trn: e.target.value })}
                           data-testid="inv-customer-trn"
                           className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#6C757D]">Tax ID type</label>
                    <input value={invForm.customer_tax_id_type} onChange={(e) => setInvForm({ ...invForm, customer_tax_id_type: e.target.value })}
                           placeholder="TRN / VAT / NIF…"
                           className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#6C757D]">Website</label>
                    <input value={invForm.customer_website} onChange={(e) => setInvForm({ ...invForm, customer_website: e.target.value })}
                           className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#6C757D]">Address</label>
                    <input value={invForm.customer_address} onChange={(e) => setInvForm({ ...invForm, customer_address: e.target.value })}
                           className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="text-xs font-bold uppercase text-[#6C757D]">Document type</label>
                    <select value={invForm.status} onChange={(e) => setInvForm({ ...invForm, status: e.target.value })}
                            data-testid="inv-status" className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                      <option value="proforma">Proforma</option>
                      <option value="draft">Draft invoice</option>
                      <option value="issued">Issued (tax invoice)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#6C757D]">VAT %</label>
                    <input type="number" value={invForm.tax_rate} onChange={(e) => setInvForm({ ...invForm, tax_rate: e.target.value })}
                           className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none" />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="text-xs font-bold uppercase text-[#6C757D]">Products &amp; quantities</label>
                  <div className="mt-2 border border-slate-200 rounded-lg divide-y max-h-56 overflow-y-auto">
                    {items.map((it) => (
                      <div key={it.id} className="flex items-center gap-3 px-3 py-2">
                        <input type="checkbox" checked={Number(selected[it.id]) > 0}
                               data-testid={`inv-select-${it.id}`}
                               onChange={(e) => setSelected({ ...selected, [it.id]: e.target.checked ? 1 : 0 })} />
                        <span className="flex-1 text-sm truncate">{it.name}</span>
                        <span className="text-xs text-slate-500">{it.currency || catalog.currency} {it.unit_price ?? '—'}</span>
                        <input type="number" min="0" value={selected[it.id] ?? 0}
                               data-testid={`inv-qty-${it.id}`}
                               onChange={(e) => setSelected({ ...selected, [it.id]: e.target.value })}
                               className="w-16 px-2 py-1 border border-slate-200 rounded text-sm focus:outline-none" />
                      </div>
                    ))}
                    {extraLines.map((r, i) => (
                      <div key={`x-${r.product_id}`} className="flex items-center gap-3 px-3 py-2 bg-cyan-50/40" data-testid={`inv-extra-${r.product_id}`}>
                        <Package className="w-4 h-4 text-[#00B4D8] flex-shrink-0" />
                        <span className="flex-1 text-sm truncate">{r.name} <span className="text-[10px] text-slate-400">({r.product_sku})</span></span>
                        <span className="text-xs text-slate-500">{r.currency} {r.unit_price}</span>
                        <input type="number" min="0" value={r.quantity}
                               onChange={(e) => setExtraLines((rows) => rows.map((x, idx) => idx === i ? { ...x, quantity: e.target.value } : x))}
                               className="w-16 px-2 py-1 border border-slate-200 rounded text-sm focus:outline-none" />
                        <button onClick={() => setExtraLines((rows) => rows.filter((_, idx) => idx !== i))}
                                className="text-slate-300 hover:text-red-500" title="Remove"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <label className="text-[11px] font-bold uppercase text-[#6C757D] mb-1 block">Add another product (Master Registry)</label>
                    <div data-testid="inv-add-product">
                      <ProductPicker value="" onSelect={addExtraProduct}
                                     placeholder="Search any product by SKU or name to add to this invoice…" />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-5">
                  <Button variant="outline" onClick={() => setShowInvoice(false)}>Cancel</Button>
                  <Button onClick={submitInvoice} disabled={invoicing} data-testid="submit-invoice"
                          className="bg-[#FF6B35] hover:bg-[#F97316]">
                    {invoicing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogDetail;
